import axios, { Canceler } from 'axios';
import { makeAutoObservable } from 'mobx';
import i18n from '../../../i18n';
import { CurrentData } from '../../../models/abstract/current-data.entity';
import {
    CurrentBusinessDataResponse,
    CurrentBusinessDataType,
} from '../../../models/abstract/current-business-data.entity';
import apiManage from '../../../request';
import { WhenError } from '../../../request/http';
import type { CurrentStatusData } from './current-status-section';
import type { RiskItem } from './restaurant-risk-section';

/** 解析 table_count 字符串 "14/58" 为 [使用中, 总数] */
function parseTableCount(s?: string): { used: number; total: number } {
    if (!s || typeof s !== 'string') return { used: 0, total: 0 };
    const parts = s.split('/').map((p) => Number.parseInt(p.trim(), 10));
    const used = Number.isNaN(parts[0]) ? 0 : parts[0];
    const total = Number.isNaN(parts[1]) ? 0 : parts[1];
    return { used, total };
}

/** 将当前营业数据接口响应转为 CurrentStatusData */
function mapCurrentStatus(res: CurrentBusinessDataResponse): CurrentStatusData {
    const status = res.current_status;
    const { used, total } = parseTableCount(status?.table_count);
    return {
        totalRevenue: status?.total_revenue ?? 0,
        tableUsed: used,
        tableTotal: total,
        takeoutOrderCount: status?.takeaway_order_count ?? 0,
        dineInCustomerCount: status?.dine_in_customer_count ?? 0,
        currentDineInCustomerCount: status?.current_dine_in_customer_count ?? 0,
    };
}

/** 风控项与 i18n key 的对应关系（接口字段 -> labelKey） */
const RISK_FIELD_TO_LABEL: Array<{ field: keyof NonNullable<CurrentBusinessDataResponse['risk_control']>; labelKey: string }> = [
    { field: 'all', labelKey: 'dashboards_data_risk_all' },
    { field: 'max_discount', labelKey: 'dashboards_data_risk_max_discount_amount' },
    { field: 'dish_return', labelKey: 'dashboards_data_risk_dish_return' },
    { field: 'order_dish_abnormal', labelKey: 'dashboards_data_risk_order_dish_abnormal' },
    { field: 'abnormal_dining_duration', labelKey: 'dashboards_data_risk_dining_duration_abnormal' },
    { field: 'quick_close', labelKey: 'dashboards_data_risk_quick_close' },
    { field: 'cancel', labelKey: 'dashboards_data_risk_cancel' },
];

/** 将 risk_control 转为 RiskItem[]（仅展示数量 > 0 的项，若全部为 0 则展示「全部」） */
function mapRiskItems(res: CurrentBusinessDataResponse): RiskItem[] {
    const risk = res.risk_control;
    if (!risk) return [];

    const items: RiskItem[] = RISK_FIELD_TO_LABEL.map(({ field, labelKey }) => ({
        labelKey,
        count: Number(risk[field]) || 0,
    }));

    const hasAny = items.some((i) => i.count > 0);
    if (hasAny) return items.filter((i) => i.count > 0);
    return [{ labelKey: 'dashboards_data_risk_all', count: 0 }];
}

/**
 * 按当前筛选时段拼接午/晚餐人数：全天为「午餐 / 晚餐」，选午餐或晚餐时仅展示对应人数。
 * @param lunchCount - 接口午餐人数
 * @param dinnerCount - 接口晚餐人数
 * @param timePeriodType - '0' 全天，'1' 午餐，'2' 晚餐
 * @returns 用于列表展示的字符串，无有效值时 undefined
 */
function mapLunchDinnerCustomerDisplay(
    lunchCount: string | undefined,
    dinnerCount: string | undefined,
    timePeriodType: CurrentBusinessDataType
): string | undefined {
    const lunch = lunchCount?.trim();
    const dinner = dinnerCount?.trim();
    if (timePeriodType === '1') {
        return lunch || undefined;
    }
    if (timePeriodType === '2') {
        return dinner || undefined;
    }
    return [lunch, dinner].filter(Boolean).join(' / ') || undefined;
}

/** 将 settled_orders + unsettled_orders 转为页面使用的 CurrentData 平铺结构 */
function mapResponseToCurrentData(
    res: CurrentBusinessDataResponse,
    timePeriodType: CurrentBusinessDataType
): CurrentData {
    const settled = res.settled_orders;
    const unsettled = res.unsettled_orders;
    const data: CurrentData = {};

    // 已结 - 汇总
    data.finish_actual_amount = settled?.actual_receipt_total;
    data.finish_discount_amount = settled?.discount_total;
    // 已结 - 堂食
    data.finish_table_num = settled?.dine_in?.table_opening_count;
    data.finish_sum_customer_num = settled?.dine_in?.customer_count;
    data.finish_time_customer_num = mapLunchDinnerCustomerDisplay(
        settled?.dine_in?.lunch_customer_count,
        settled?.dine_in?.dinner_customer_count,
        timePeriodType
    );
    data.finish_dine_in_actual_amount = settled?.dine_in?.order_total;
    // 已结 - 外卖
    data.finish_delivery_num = settled?.takeaway?.order_count;
    data.finish_delivery_actual_amount = settled?.takeaway?.total;
    // 已结 - 菜品
    data.total_quantity = settled?.dishes?.ordered_count;
    data.total_quantity_rush = settled?.dishes?.urged_count;
    data.items = settled?.dishes?.returned_count;
    data.amount = settled?.dishes?.returned_total;
    data.reprice_num = settled?.dishes?.price_adjustment_count;
    data.reprice_amount = settled?.dishes?.price_adjustment_difference;
    data.refunds_total = settled?.dishes?.anti_settlement_count;
    data.finish_service_amount = settled?.dishes?.service_fee;
    data.tip_amount = settled?.dishes?.tip_amount ?? 0;

    // 未结 - 汇总
    data.unpaid_original_amount = unsettled?.accounts_receivable_total;
    data.unpaid_paid_amount = unsettled?.paid_amount_total;
    // 未结 - 堂食
    data.using_table_num = unsettled?.dine_in?.table_opening_count;
    data.sum_customer_num = unsettled?.dine_in?.customer_count;
    data.time_customer_num = mapLunchDinnerCustomerDisplay(
        unsettled?.dine_in?.lunch_customer_count,
        unsettled?.dine_in?.dinner_customer_count,
        timePeriodType
    );
    data.dine_in_order_total = unsettled?.dine_in?.order_total;
    // 未结 - 外卖
    data.delivery_num = unsettled?.takeaway?.order_count;
    data.takeaway_order_total = unsettled?.takeaway?.total;
    // 未结 - 菜品
    data.total_dish = unsettled?.dishes?.ordered_count;
    data.rush_dish = unsettled?.dishes?.urged_count;
    data.cancelled_dish = unsettled?.dishes?.returned_count;
    data.refund_dish_total = unsettled?.dishes?.returned_total;

    return data;
}

class DashboardsDataStore {
    /** 是否加载中 */
    loading: boolean = true;
    /**
     * 已结/未结分析区骨架：在切换午/晚/全天等非首屏拉数时为 true，避免与全页首屏 `loading` 同时出现。
     * 定时轮询不触发，避免每分钟闪动。
     */
    analysisSectionLoading = false;
    /** 已结/未结卡片使用的平铺数据（由新接口 settled_orders + unsettled_orders 映射） */
    data: CurrentData = {};
    /** 当前营业状态（总营收、桌台、外卖单数、堂食人数） */
    currentStatusData: CurrentStatusData = {
        totalRevenue: 0,
        tableUsed: 0,
        tableTotal: 0,
        takeoutOrderCount: 0,
        dineInCustomerCount: 0,
        currentDineInCustomerCount: 0,
    };
    /** 餐厅风控项列表 */
    riskItems: RiskItem[] = [];
    /**
     * 时间段：'0' 全天，'1' 午餐，'2' 晚餐。
     * 默认全天；用户手动切换后保持其选择直至离开页。
     */
    timePeriodType: CurrentBusinessDataType = '0';
    /** 是否已由用户手动切换过时段；未手动切换时始终保持默认全天。 */
    hasManualTimePeriodSelection = false;
    /** 请求失败时的错误信息 */
    errorMessage = '';
    /** 取消请求的 Cancel 函数 */
    canceler: Canceler | null = null;
    /**
     * 仅当 `fetchData(..., true)` 带已结/未结骨架时递增，用于并发/快速切换时只让**最后一次**
     * 此类请求在 `finally` 中关闭骨架（与定时轮询共用 `fetchData` 时不应干扰）。
     */
    private lastAnalysisFetchSeq = 0;

    constructor() {
        makeAutoObservable(this);
    }

    /**
     * 拉取当前营业数据（新接口：当前状态、风控、已结/未结）
     * @param stringId 餐厅码
     * @param loadingValue 是否全页 loading（首屏）
     * @param showAnalysisSkeleton 非首屏时是否在已结/未结分析卡片区展示骨架（如切换时段）；定时刷新传 false
     */
    async fetchData(stringId: string, loadingValue: boolean, showAnalysisSkeleton = false) {
        /** 未手动切换时段时同步为默认全天（不因驻留时长改回午餐/晚餐）。 */
        this.syncDefaultTimePeriodType();
        this.setLoading(loadingValue);
        /** 本请求若为「仅分析区骨架」拉数，分配独立序号，供 `finally` 与后续同类请求去重。 */
        const analysisRequestSeq = showAnalysisSkeleton ? ++this.lastAnalysisFetchSeq : 0;
        if (showAnalysisSkeleton) {
            this.setAnalysisSectionLoading(true);
        }
        this?.canceler?.();
        const cancelToken = new axios.CancelToken((c) => (this.canceler = c));
        try {
            const res = await apiManage.get_current_business_data(
                { string_id: stringId, type: this.timePeriodType },
                { cancelToken }
            );
            if (!res?.error) {
                const raw = res as CurrentBusinessDataResponse;
                this.setData(mapResponseToCurrentData(raw, this.timePeriodType));
                this.setCurrentStatusData(mapCurrentStatus(raw));
                this.setRiskItems(mapRiskItems(raw));
                this.setErrorMessage('');
            } else {
                const error = res as WhenError;
                if (error.message !== 'canceled') {
                    if (error?.result?.statusCode === 404) {
                        this.setErrorMessage(i18n.t('business_analysis_page_msg_no_database_config'));
                    } else {
                        this.setErrorMessage(i18n.t('business_analysis_page_msg_fetch_immediate_info_failed'));
                    }
                    this.setData({} as CurrentData);
                    this.setCurrentStatusData({
                        totalRevenue: 0,
                        tableUsed: 0,
                        tableTotal: 0,
                        takeoutOrderCount: 0,
                        dineInCustomerCount: 0,
                        currentDineInCustomerCount: 0,
                    });
                    this.setRiskItems([]);
                }
            }
        } finally {
            this.setLoading(false);
            if (showAnalysisSkeleton && analysisRequestSeq === this.lastAnalysisFetchSeq) {
                this.setAnalysisSectionLoading(false);
            }
        }
    }

    /** 仅在用户未手动切换时，将时段恢复为默认全天。 */
    syncDefaultTimePeriodType() {
        if (this.hasManualTimePeriodSelection) {
            return;
        }
        this.timePeriodType = '0';
    }

    /** 设置时间段并触发重新拉取（由页面在切换筛选时调用） */
    setTimePeriodType(value: CurrentBusinessDataType) {
        this.timePeriodType = value;
        this.hasManualTimePeriodSelection = true;
    }

    setData(value: CurrentData) {
        this.data = value;
    }

    setCurrentStatusData(value: CurrentStatusData) {
        this.currentStatusData = value;
    }

    setRiskItems(value: RiskItem[]) {
        this.riskItems = value;
    }

    setLoading(value: boolean) {
        this.loading = value;
    }

    setAnalysisSectionLoading(value: boolean) {
        this.analysisSectionLoading = value;
    }

    setErrorMessage(value: string) {
        this.errorMessage = value;
    }
}

export default DashboardsDataStore;
